import { AlertColor, Button, useTheme } from "@mui/material";
import BaseDialog, { BaseDialogProps } from "./BaseDialog";
import { CustomDialogType } from "./types";
import { ReactNode } from "react";
import { preservePunctuation } from "utils/common.utils";
import { useTranslation } from "react-i18next";

export interface YesNoDialogProps
  extends Omit<
    BaseDialogProps,
    "onCloseDialogClicked" | "children" | "sx" | "alertColor" | "icon"
  > {
  noButtonText?: string;
  onNoButtonClicked: () => void;
  onYesButtonClicked: () => void;
  type: CustomDialogType;
  yesButtonText?: string;
  yesButtonStartIcon?: ReactNode;
}

export default function YesNoDialog(props: YesNoDialogProps) {
  const {
    noButtonText = "No",
    onNoButtonClicked,
    onYesButtonClicked,
    type = "default",
    yesButtonText = "Yes",
    yesButtonStartIcon,
    ...baseDialogProps
  } = props;
  const { t: translate } = useTranslation();
  const theme = useTheme();

  const getAlertColour = (): AlertColor => {
    switch (type) {
      case "warning":
        return "warning";
      case "error":
        return "error";
      case "success":
        return "success";
      default:
        return "info";
    }
  };

  const getButtonColour = () => {
    switch (type) {
      case "warning":
        return "warning";
      case "info":
        return "info";
      case "error":
        return "error";
      case "success":
        return "success";
      case "default":
        return "inherit";
    }
  };

  return (
    <BaseDialog
      alertColor={getAlertColour()}
      {...baseDialogProps}
      onCloseDialogClicked={onNoButtonClicked}
      actionButtons={
        <>
          <Button
            variant="text"
            color="primary"
            onClick={onNoButtonClicked}
            sx={{ mr: 1, textTransform: "none" }}
            size="medium"
          >
            {preservePunctuation(`${translate(noButtonText)}`)}
          </Button>
          <Button
            variant="contained"
            color={getButtonColour()}
            sx={{ color: theme.palette.primary.contrastText, textTransform: "none" }}
            onClick={onYesButtonClicked}
            size="medium"
            startIcon={yesButtonStartIcon}
          >
            {preservePunctuation(`${translate(yesButtonText)}`)}
          </Button>
        </>
      }
    />
  );
}
